/**
 * @file myTodosLogic.ts
 * @brief Pure helpers for "My to-dos": group labels, due-date text, the
 *        second line's "who".
 *
 * @details
 * The phone is the reference (DES 12-10-2026). The backend groups the rows
 * (`GET /boardroom/my-todos`, in the browser's own `tz`); the web only words
 * them:
 * - Group labels: Overdue, Today, Tomorrow, This week, Later, No due date.
 * - Due date on the right of line 1: none for Today / Tomorrow (the group
 *   says it), "Thu 16 Oct" within the week, else "9 Oct" (plus the year when
 *   it is not this year's).
 * - Line 2's "who": nothing for my own task (was "You"), "Follow up" when I follow up an
 *   external person's task, else the assignee's name.
 * - To-do-only rows: "from <name>" (else "the meeting owner") and the
 *   meeting's date (12-10-2026).
 *
 * No imports, so `node --test` runs it as is.
 *
 * @author DES
 * @date 12-10-2026
 */

export type MyTodoGroupKey = "overdue" | "today" | "tomorrow" | "week" | "later" | "none" | "done";

export type MyTodoRole = "assignee" | "followUp" | "owner" | "viewer";

export const MY_TODO_GROUP_LABELS: Record<MyTodoGroupKey, string> = {
  overdue: "Overdue",
  today: "Today",
  tomorrow: "Tomorrow",
  week: "This week",
  later: "Later",
  none: "No due date",
  done: "Done",
};

/** A label for any key; an unknown one (a future group) is shown as sent. */
export function groupLabel(key: string): string {
  return (MY_TODO_GROUP_LABELS as Record<string, string>)[key] ?? key;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** `YYYY-MM-DD` → its parts, read as a calendar day (no time zone shift). */
function parseDay(value: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return { y, m, d };
}

/**
 * The due date beside the task. Empty for the Today and Tomorrow groups and
 * for a task with no date. `today` is the server's `YYYY-MM-DD`, so "this
 * year" is the user's year, not the browser clock's.
 */
export function dueDateText(
  dueDate: string | null | undefined,
  group: string,
  today: string | null | undefined,
): string {
  if (!dueDate || group === "today" || group === "tomorrow") return "";
  const day = parseDay(dueDate);
  if (!day) return "";
  const base = `${day.d} ${MONTHS[day.m - 1]}`;
  if (group === "week") {
    const weekday = WEEKDAYS[new Date(Date.UTC(day.y, day.m - 1, day.d)).getUTCDay()];
    return `${weekday} ${base}`;
  }
  const thisYear = today ? parseDay(today)?.y : undefined;
  return thisYear !== undefined && thisYear !== day.y ? `${base} ${day.y}` : base;
}

/** Line 2's "who": "Follow up", or the assignee's name (may be ""). Nothing
 *  for my own (DES 14-10-2026, phone and web: "yeh todos user kay hi hain";
 *  was "You" on every row). */
export function whoText(
  role: string,
  assigneeName: string | null | undefined,
): string {
  if (role === "assignee") return "";
  if (role === "followUp") return "Follow up";
  return (assigneeName ?? "").trim();
}

/** Who gave a to-do-only user their to-do; the owner when the API has no name. */
export function fromText(fromName: string | null | undefined): string {
  return fromName?.trim() || "the meeting owner";
}

/**
 * The meeting's date on a to-do-only row and card: "9 Oct", plus the year
 * when it is not `today`'s year. Read in the browser's zone, as the server
 * buckets in it. Empty for a missing or broken value.
 */
export function meetingDateText(
  iso: string | null | undefined,
  today: string | null | undefined,
): string {
  if (!iso) return "";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const base = `${at.getDate()} ${MONTHS[at.getMonth()]}`;
  const thisYear = today ? parseDay(today)?.y : undefined;
  return thisYear !== undefined && thisYear !== at.getFullYear() ? `${base} ${at.getFullYear()}` : base;
}

// ----------------------------------------------------------------------
// By meeting (DES 14-10-2026, "By date / By meeting" switch; phone has the
// same rules in FRONTEND myTodosApi.ts groupTodosByMeeting)
// ----------------------------------------------------------------------

/** F1a (31-10-2026) adds "category" and "person" (todoViews.ts). */
export type TodoGrouping = "date" | "meeting" | "category" | "person";

type GroupableItem = {
  meetingId: string;
  meetingTitle?: string | null;
  meetingDate?: string | null;
  dueDate?: string | null;
  group: string;
};

export type MeetingGroup<T extends GroupableItem> = {
  meetingId: string;
  meetingTitle: string;
  meetingDate: string | null;
  items: T[];
  overdue: number;
};

const GROUP_RANK: Record<string, number> = {
  overdue: 0,
  today: 1,
  tomorrow: 2,
  week: 3,
  later: 4,
  none: 5,
  done: 6,
};

function compareDue(a: GroupableItem, b: GroupableItem): number {
  const g = (GROUP_RANK[a.group] ?? 5) - (GROUP_RANK[b.group] ?? 5);
  if (g) return g;
  if (a.dueDate && b.dueDate) return a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0;
  if (a.dueDate) return -1;
  if (b.dueDate) return 1;
  return 0;
}

/**
 * The server's date groups, regrouped by meeting. Inside a meeting by due
 * date (no date last); meetings with something overdue first, then the
 * soonest next due date, then no-date meetings, newest meeting first.
 */
export function groupByMeeting<T extends GroupableItem>(
  groups: { items: T[] }[],
): MeetingGroup<T>[] {
  const byId = new Map<string, MeetingGroup<T>>();
  for (const g of groups) {
    for (const item of g.items) {
      let m = byId.get(item.meetingId);
      if (!m) {
        m = {
          meetingId: item.meetingId,
          meetingTitle: item.meetingTitle?.trim() || "Untitled meeting",
          meetingDate: item.meetingDate ?? null,
          items: [],
          overdue: 0,
        };
        byId.set(item.meetingId, m);
      }
      m.items.push(item);
      if (item.group === "overdue") m.overdue += 1;
    }
  }
  const out = [...byId.values()];
  for (const m of out) m.items.sort(compareDue);
  const at = (m: MeetingGroup<T>) => (m.meetingDate ? new Date(m.meetingDate).getTime() || 0 : 0);
  out.sort((a, b) => compareDue(a.items[0], b.items[0]) || at(b) - at(a));
  return out;
}

/** A row's date under a meeting heading: also "Today" / "Tomorrow". */
export function dueDateTextByMeeting(
  dueDate: string | null | undefined,
  group: string,
  today: string | null | undefined,
): string {
  if (group === "today") return "Today";
  if (group === "tomorrow") return "Tomorrow";
  return dueDateText(dueDate, group, today);
}

// ----------------------------------------------------------------------
// Due-date filter (DES 14-10-2026, in the same menu; phone: filterTodosByDue)
// ----------------------------------------------------------------------

export type DueFilter = "any" | "today" | "week" | "next7" | "month";

export const DUE_FILTER_LABEL: Record<DueFilter, string> = {
  any: "Any date",
  today: "Due today",
  week: "Due this week",
  next7: "Next 7 days",
  month: "This month",
};

function addDays(day: string, n: number): string {
  const p = parseDay(day);
  if (!p) return day;
  return new Date(Date.UTC(p.y, p.m - 1, p.d + n)).toISOString().slice(0, 10);
}

/** Keeps the to-dos due in the range; overdue always stays, no-date goes
 *  once a range is set. `today` is the server's YYYY-MM-DD. */
export function filterByDue<T extends { group: string; dueDate?: string | null }>(
  groups: { key: string; items: T[] }[],
  filter: DueFilter,
  today: string | null | undefined,
): { key: string; items: T[] }[] {
  if (filter === "any" || !today) return groups;
  const keep = (it: T): boolean => {
    if (it.group === "overdue") return true;
    if (!it.dueDate) return false;
    if (filter === "today") return it.group === "today";
    if (filter === "week") return it.group === "today" || it.group === "tomorrow" || it.group === "week";
    if (filter === "next7") return it.dueDate >= today && it.dueDate <= addDays(today, 6);
    return it.dueDate >= today && it.dueDate.slice(0, 7) === today.slice(0, 7);
  };
  return groups.map((g) => ({ ...g, items: g.items.filter(keep) })).filter((g) => g.items.length > 0);
}
